import React, { useContext, useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";
import "../../../assets/css/gcstudent/studentdashboardpage/StudentDashboardPage.css";
import AppContext from "../../../context/AppContext";

/* ── Small inline icon set ── */
const Icon = ({ name, ...props }) => {
  const common = { width: 22, height: 22, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", ...props };
  switch (name) {
    case "book":
      return <svg {...common}><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>;
    case "bookmark":
      return <svg {...common}><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="9" y1="12" x2="12" y2="12"/></svg>;
    case "alertTriangle":
      return <svg {...common}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
    case "checkCircle":
      return <svg {...common}><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
    case "library":
      return <svg {...common}><path d="M4 19V5a2 2 0 012-2h2a2 2 0 012 2v14"/><path d="M14 19V9a2 2 0 012-2h2a2 2 0 012 2v10"/><line x1="2" y1="19" x2="22" y2="19"/></svg>;
    case "spark":
      return <svg {...common}><path d="M12 2l1.6 5.6L19 9l-5.4 1.4L12 16l-1.6-5.6L5 9l5.4-1.4z"/></svg>;
    case "heart":
      return <svg {...common}><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 10-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>;
    case "zap":
      return <svg {...common}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
    case "bell":
      return <svg {...common}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>;
    case "alertCircle":
      return <svg {...common}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
    case "clock":
      return <svg {...common} width={16} height={16}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
    case "arrowRight":
      return <svg {...common}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>;
    default:
      return null;
  }
};

/* ── Quick Actions (static config, dynamic navigation) ──
   NOTE: "Browse Library" points to the public /library route since
   there's no dedicated /student/browse-books route wired up yet.
   Add one in App.js and change the `to` below if you want it separate. */
const quickActions = [
  { label: "Browse Library", icon: "library", colorClass: "gcstudent-qa--blue",   to: "/library" },
  { label: "AI Assistant",   icon: "spark",   colorClass: "gcstudent-qa--purple", to: "/student/libraro" },
  { label: "Saved Notes",    icon: "heart",   colorClass: "gcstudent-qa--green",  to: "/student/saved-notes" },
  { label: "Flash Cards",    icon: "zap",     colorClass: "gcstudent-qa--yellow", to: "/student/flash-cards" },
];

/* ── Status badge colour mapping for disputes ── */
const DISPUTE_STATUS_CLASS = {
  open:        "gcstudent-badge--warning",
  pending:     "gcstudent-badge--warning",
  resolved:    "gcstudent-badge--success",
  approved:    "gcstudent-badge--success",
  "in review": "gcstudent-badge--info",
  rejected:    "gcstudent-badge--danger",
};

/* ── Animated Counter ── */
const Counter = ({ target, visible }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!visible) return;
    let n = 0;
    const step = Math.ceil((target || 0) / 40) || 1;
    const t = setInterval(() => {
      n += step;
      if (n >= target) { setVal(target); clearInterval(t); }
      else setVal(n);
    }, 18);
    return () => clearInterval(t);
  }, [visible, target]);
  return <>{val}</>;
};

/* ── Clock icon per alert type ── */
const AlertClockIcon = ({ type }) => {
  const cls = `gcstudent-clock-icon gcstudent-clock-icon--${type}`;
  if (type === "success")
    return <span className={cls}><Icon name="checkCircle" width={16} height={16} strokeWidth={2.2} /></span>;
  return <span className={cls}><Icon name="clock" width={16} height={16} strokeWidth={2.2} /></span>;
};

/* ── Format date ── */
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
};

/* ══════════════════════════════════════
   Dashboard Page
══════════════════════════════════════ */
const StudentDashboardPage = () => {
  const { loggedInUser, baseUrl, authToken } = useContext(AppContext);
  const studentName = loggedInUser?.fullName || "Student";

  /* ── Animation states ── */
  const [pageIn,   setPageIn]   = useState(false);
  const [statsIn,  setStatsIn]  = useState(false);
  const [qaIn,     setQaIn]     = useState(false);
  const [fineIn,   setFineIn]   = useState(false);
  const [panelsIn, setPanelsIn] = useState(false);

  /* ── Dynamic data states ── */
  const [stats, setStats] = useState({ totalIssued: 0, totalReserved: 0, totalOverdue: 0 });
  const [disputes, setDisputes]               = useState([]);
  const [returnAlerts, setReturnAlerts]        = useState([]);
  const [fineData, setFineData]                = useState({ totalFine: 0, bookTitle: null });

  const [statsLoading, setStatsLoading]         = useState(true);
  const [disputesLoading, setDisputesLoading]   = useState(true);
  const [alertsLoading, setAlertsLoading]        = useState(true);
  const [fineLoading, setFineLoading]           = useState(true);

  /* ── Refs for intersection observers ── */
  const statsRef  = useRef(null);
  const qaRef     = useRef(null);
  const fineRef   = useRef(null);
  const panelsRef = useRef(null);

  /* ── Page mount animation ── */
  useEffect(() => { setTimeout(() => setPageIn(true), 50); }, []);

  /* ── Intersection observers ── */
  useEffect(() => {
    const mkObs = (setter) =>
      new IntersectionObserver(([e]) => { if (e.isIntersecting) setter(true); }, { threshold: 0.1 });
    const s = mkObs(setStatsIn);  if (statsRef.current)  s.observe(statsRef.current);
    const q = mkObs(setQaIn);     if (qaRef.current)     q.observe(qaRef.current);
    const f = mkObs(setFineIn);   if (fineRef.current)   f.observe(fineRef.current);
    const p = mkObs(setPanelsIn); if (panelsRef.current) p.observe(panelsRef.current);
    return () => { s.disconnect(); q.disconnect(); f.disconnect(); p.disconnect(); };
  }, []);

  /* ── Fetch dashboard stats ── */
  useEffect(() => {
    if (!loggedInUser?.id) { setStatsLoading(false); return; }

    const formData = new FormData();
    formData.append("lbstudent_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/student/dashboard/fetchStudentStatsForDashboard`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.success) {
          setStats({
            totalIssued:   res.data.totalIssued   ?? 0,
            totalReserved: res.data.totalReserved ?? 0,
            totalOverdue:  res.data.totalOverdue  ?? 0,
          });
        }
      })
      .catch((err) => console.error("Failed to fetch student stats:", err))
      .finally(() => setStatsLoading(false));
  }, [baseUrl, authToken, loggedInUser]);

  /* ── Fetch outstanding fine ── */
  useEffect(() => {
    if (!loggedInUser?.id) { setFineLoading(false); return; }

    const formData = new FormData();
    formData.append("lbstudent_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/student/dashboard/fetchStudentOutstandingFine`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.success) {
          setFineData({
            totalFine: res.data.totalFine ?? 0,
            bookTitle: res.data.bookTitle ?? null,
          });
        }
      })
      .catch((err) => console.error("Failed to fetch outstanding fine:", err))
      .finally(() => setFineLoading(false));
  }, [baseUrl, authToken, loggedInUser]);

  /* ── Fetch return date alerts ── */
  useEffect(() => {
    if (!loggedInUser?.id) { setAlertsLoading(false); return; }

    const formData = new FormData();
    formData.append("lbstudent_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/student/dashboard/fetchStudentReturnAlerts`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.success) {
          setReturnAlerts(res.data.alerts ?? []);
        }
      })
      .catch((err) => console.error("Failed to fetch return alerts:", err))
      .finally(() => setAlertsLoading(false));
  }, [baseUrl, authToken, loggedInUser]);

  /* ── Fetch recent disputes ── */
  useEffect(() => {
    if (!loggedInUser?.id) { setDisputesLoading(false); return; }

    const formData = new FormData();
    formData.append("lbstudent_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/student/disputes/fetchAllDisputes`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.success) {
          // Show only latest 3 on dashboard
          setDisputes((res.data.disputes ?? []).slice(0, 3));
        }
      })
      .catch((err) => console.error("Failed to fetch disputes:", err))
      .finally(() => setDisputesLoading(false));
  }, [baseUrl, authToken, loggedInUser]);

  /* ── Stat card config (dynamic values injected) ── */
  const statCards = [
    { label: "Books Issued",  value: stats.totalIssued,   subLabel: "Currently issued", icon: "book",          colorClass: "gcstudent-stat--teal"   },
    { label: "Reservations",  value: stats.totalReserved, subLabel: "Pending approval",  icon: "bookmark",      colorClass: "gcstudent-stat--orange" },
    { label: "Overdue",       value: stats.totalOverdue,  subLabel: "Return soon",        icon: "alertTriangle", colorClass: "gcstudent-stat--red"    },
  ];

  return (
    <div className={`gcstudent-page ${pageIn ? "gcstudent-page--in" : ""}`}>
      <Container fluid className="gcstudent-container">

        {/* ── Hero / Welcome Banner ── */}
        <div className="gcstudent-hero">
          <div className="gcstudent-hero__left">
            <span className="gcstudent-hero__badge">
              <Icon name="spark" width={14} height={14} />
              Student Dashboard
            </span>
            <h2 className="gcstudent-hero__title">Welcome back, {studentName}!</h2>
            <p className="gcstudent-hero__sub">Here's an overview of your library activity today.</p>
          </div>
          <Link to="/library" className="gcstudent-hero__cta">
            Browse Books <Icon name="arrowRight" width={15} height={15} strokeWidth={2.2} />
          </Link>
        </div>

        {/* ── Stat Cards (Dynamic) ── */}
        <Row className="gy-3 gx-3 mb-3" ref={statsRef}>
          {statCards.map((card, i) => (
            <Col xs={12} sm={4} key={card.label}>
              <div
                className={`gcstudent-stat ${statsIn ? "gcstudent-stat--in" : ""}`}
                style={{ transitionDelay: `${0.04 + i * 0.08}s` }}
              >
                <div className="gcstudent-stat__info">
                  <p className="gcstudent-stat__label">{card.label}</p>
                  <h3 className="gcstudent-stat__value">
                    {statsLoading ? "—" : <Counter target={card.value} visible={statsIn} />}
                  </h3>
                  <p className="gcstudent-stat__sub">{card.subLabel}</p>
                </div>
                <div className={`gcstudent-stat__icon ${card.colorClass}`}>
                  <Icon name={card.icon} />
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* ── Quick Actions ── */}
        <div className={`gcstudent-qa ${qaIn ? "gcstudent-qa--in" : ""}`} ref={qaRef}>
          <h6 className="gcstudent-qa__title">Quick Actions</h6>
          <div className="gcstudent-qa__grid">
            {quickActions.map((action, i) => (
              <Link
                key={action.label}
                to={action.to}
                className={`gcstudent-qa__btn ${qaIn ? "gcstudent-qa__btn--in" : ""}`}
                style={{ transitionDelay: `${0.08 + i * 0.07}s` }}
              >
                <span className={`gcstudent-qa__icon ${action.colorClass}`}>
                  <Icon name={action.icon} width={26} height={26} strokeWidth={1.6} />
                </span>
                <span className="gcstudent-qa__label">{action.label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* ── Outstanding Fine Alert (Dynamic) ── */}
        {!fineLoading && fineData.totalFine > 0 && (
          <div
            ref={fineRef}
            className={`gcstudent-fine-alert ${fineIn ? "gcstudent-fine-alert--in" : ""}`}
          >
            <div className="gcstudent-fine-alert__icon-wrap">
              <Icon name="alertTriangle" width={16} height={16} strokeWidth={2.2} />
            </div>
            <div>
              <p className="gcstudent-fine-alert__title">Outstanding Fine</p>
              <p className="gcstudent-fine-alert__body">
                You have a fine of{" "}
                <span className="gcstudent-fine-alert__amount">Rs. {fineData.totalFine}</span>
                {fineData.bookTitle ? ` for late return of "${fineData.bookTitle}".` : "."}
              </p>
            </div>
          </div>
        )}

        {/* ── Bottom Row: Return Alerts + Recent Disputes ── */}
        <Row className="gy-3 gx-3" ref={panelsRef}>

          {/* ── Return Date Alerts (Dynamic) ── */}
          <Col xs={12} lg={7}>
            <div
              className={`gcstudent-panel ${panelsIn ? "gcstudent-panel--in" : ""}`}
              style={{ transitionDelay: "0.08s" }}
            >
              <div className="gcstudent-panel__header">
                <Icon name="bell" width={15} height={15} strokeWidth={2} className="gcstudent-panel__header-icon" />
                <h5>Return Date Alerts</h5>
              </div>
              <div className="gcstudent-panel__body">
                {alertsLoading ? (
                  <p className="gcstudent-chat-item__date" style={{ padding: "12px 20px" }}>
                    Loading return alerts...
                  </p>
                ) : returnAlerts.length === 0 ? (
                  <p className="gcstudent-chat-item__date" style={{ padding: "12px 20px" }}>
                    No books currently issued.
                  </p>
                ) : (
                  returnAlerts.map((alert, i) => (
                    <div
                      className={`gcstudent-return-item ${panelsIn ? "gcstudent-return-item--in" : ""}`}
                      key={alert.id}
                      style={{ transitionDelay: `${0.15 + i * 0.1}s` }}
                    >
                      <div className="gcstudent-return-item__left">
                        <AlertClockIcon type={alert.type} />
                        <div>
                          <p className="gcstudent-return-item__title">{alert.title}</p>
                          <p className="gcstudent-return-item__due">Due: {formatDate(alert.due)}</p>
                        </div>
                      </div>
                      <span className={`gcstudent-badge gcstudent-badge--${alert.type}`}>
                        {alert.badge}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Col>

          {/* ── Recent Disputes (Dynamic, admin-style design) ── */}
          <Col xs={12} lg={5}>
            <div
              className={`gcstudent-panel ${panelsIn ? "gcstudent-panel--in" : ""}`}
              style={{ transitionDelay: "0.16s" }}
            >
              <div className="gcstudent-panel__header">
                <Icon name="alertCircle" width={16} height={16} strokeWidth={2} className="gcstudent-panel__header-icon" />
                <h5>Recent Disputes</h5>
                <Link to="/student/disputes" className="gcstudent-panel__view-all ms-auto">View All →</Link>
              </div>

              <div className="gcstudent-panel__body">
                {disputesLoading ? (
                  <p className="gcstudent-chat-item__date" style={{ padding: "12px 20px" }}>
                    Loading disputes...
                  </p>
                ) : disputes.length === 0 ? (
                  <p className="gcstudent-chat-item__date" style={{ padding: "12px 20px" }}>
                    No recent disputes found.
                  </p>
                ) : (
                  disputes.map((d, i) => (
                    <div
                      className={`gcstudent-dispute-row ${panelsIn ? "gcstudent-dispute-row--in" : ""}`}
                      key={d.id}
                      style={{ transitionDelay: `${0.22 + i * 0.1}s` }}
                    >
                      <div className="gcstudent-dispute-left">
                        <div className="gcstudent-dispute-icon">
                          <Icon name="alertCircle" width={14} height={14} strokeWidth={2} />
                        </div>
                        <div>
                          <div className="gcstudent-dispute-head">
                            <span className="gcstudent-dispute-subject">{d.title ?? d.subject}</span>
                            <span className="gcstudent-dispute-ticket">
                              #DSP-{String(d.id).padStart(3, "0")}
                            </span>
                          </div>
                          {d.description && (
                            <p className="gcstudent-dispute-desc">
                              {d.description.length > 65
                                ? d.description.substring(0, 65) + "..."
                                : d.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="gcstudent-dispute-right">
                        <span
                          className={`gcstudent-badge ${DISPUTE_STATUS_CLASS[d.status?.toLowerCase()] ?? "gcstudent-badge--warning"}`}
                        >
                          {d.status}
                        </span>
                        <span className="gcstudent-chat-item__date">{formatDate(d.created_at)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Col>

        </Row>
      </Container>
    </div>
  );
};

export default StudentDashboardPage;